import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { DiamondMark } from '../components/DiamondMark';
export function NotAFit() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Wire to ActiveCampaign "Below-Threshold-Nurture" list with Below-AUM-Threshold tag
    setSubmitted(true);
  };
  return <div className="min-h-screen bg-bg-primary text-ink-primary font-sans selection:bg-accent selection:text-ink-dark" data-id="element-50">
      {/* Section 1 — The Acknowledgment */}
      <section className="w-full px-6 md:px-12 lg:px-24 pt-20 md:pt-32 pb-16 md:pb-24" data-id="element-51">
        <div className="max-w-3xl mx-auto text-center" data-id="element-52">
          <motion.span initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} className="block text-xs md:text-sm font-semibold tracking-[0.2em] uppercase text-ink-secondary mb-6" data-id="element-53">
            RIA Catalyst
          </motion.span>
          <motion.h1 initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeOut',
          delay: 0.1
        }} className="font-display font-extrabold text-[36px] md:text-[56px] leading-[1.1] tracking-tightest text-ink-primary mb-8" data-id="element-54">
            We Appreciate You Stopping By.
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeOut',
          delay: 0.2
        }} className="text-lg md:text-[22px] leading-relaxed text-ink-secondary max-w-2xl mx-auto" data-id="element-55">
            RIA Catalyst is built specifically for wealth advisors managing
            $250M+ in AUM at wirehouses, captive bank teams, and PE-backed RIAs.
            Based on your responses, we're not the right fit for your current
            situation — and we'd rather tell you that directly than waste your
            time on a call that doesn't serve you.
          </motion.p>
        </div>
      </section>

      {/* Section 2 — The Honest Reasoning */}
      <section className="w-full px-6 md:px-12 lg:px-24 py-16 md:py-24" data-id="element-56">
        <div className="max-w-[720px] mx-auto border-t border-b border-border-subtle py-16 md:py-20" data-id="element-57">
          <motion.span initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} className="block text-xs md:text-sm font-semibold tracking-[0.2em] uppercase text-ink-secondary mb-6" data-id="element-58">
            Why The Threshold
          </motion.span>
          <motion.h2 initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.3,
          ease: 'easeOut',
          delay: 0.1
        }} className="font-display font-bold text-[28px] md:text-[36px] leading-tight tracking-tightest text-ink-primary mb-10" data-id="element-59">
            Why We Hold The Line At $250M.
          </motion.h2>
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.3,
          ease: 'easeOut',
          delay: 0.2
        }} className="text-lg md:text-xl text-ink-secondary space-y-6" style={{
          lineHeight: 1.6
        }} data-id="element-60">
            <p data-id="element-61">
              The transition we negotiate isn't a generic recruiter placement.
              It's a structured deal — transition capital, equity participation,
              multi-year compensation guarantees, client protection
              architecture, and legal sequencing through Protocol and
              non-Protocol exits.
            </p>
            <p data-id="element-62">
              The independent RIAs we work with deploy this capital for books
              that justify the complexity. For a $250M+ book, the receiving firm
              pays a fee that covers our work and still creates a strong return
              for them. Below that threshold, the economics don't work — for the
              receiving firm, for us, or for the advisor.
            </p>
            <p data-id="element-63">
              We could pretend otherwise and take the call anyway. We don't,
              because the advisor walks away with nothing actionable. That's
              worse than not having the call at all.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Section 3 — The Open Door (Email Capture) */}
      <section className="w-full px-6 md:px-12 lg:px-24 py-16 md:py-24" data-id="element-64">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} className="max-w-[560px] mx-auto bg-bg-secondary border rounded-2xl p-8 md:p-12" style={{
        borderColor: 'rgba(245, 197, 24, 0.18)'
      }} data-id="element-65">
          <div className="inline-flex items-center gap-2 mb-5" data-id="element-66">
            <span className="text-accent text-[10px]" data-id="element-67">●</span>
            <span className="text-xs md:text-sm font-semibold tracking-[0.2em] uppercase text-accent" data-id="element-68">
              When You're Ready
            </span>
          </div>
          <h3 className="font-display font-bold text-[24px] md:text-[28px] tracking-tightest text-ink-primary mb-5" data-id="element-69">
            Stay In Touch.
          </h3>
          <p className="text-[17px] leading-relaxed text-ink-secondary mb-8" data-id="element-70">
            If you're growing toward the $250M threshold, drop your email below.
            We'll let you know when we expand our service tiers — and we'll send
            a quarterly market briefing covering compensation benchmarks, the
            firms actively deploying capital, and how the independent landscape
            is shifting.
          </p>
          <p className="text-[17px] leading-relaxed text-ink-secondary mb-8" data-id="element-71">
            No pitch. No sales follow-up. Just the data, when there's something
            worth sharing.
          </p>

          {submitted ? <div className="bg-accent-soft border border-accent/30 rounded-lg px-5 py-4 text-ink-primary text-[15px]" data-id="element-72">
              You're on the list. We'll be in touch when there's something worth
              sharing.
            </div> : <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3" data-id="element-73">
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" className="flex-1 bg-bg-primary border border-border-subtle rounded-full px-5 py-3 text-ink-primary placeholder-ink-secondary/50 focus:outline-none focus:border-accent transition-colors duration-200" aria-label="Email address" data-id="element-74" />
              <button type="submit" className="px-6 py-3 bg-accent text-bg-primary font-semibold rounded-full whitespace-nowrap transition-all duration-200 hover:scale-[1.02] hover:shadow-[0_8px_24px_rgba(245,197,24,0.3)]" data-id="element-75">
                Keep me posted →
              </button>
            </form>}

          <p className="text-[13px] text-ink-secondary mt-5" data-id="element-76">
            One email per quarter, maximum. Unsubscribe anytime.
          </p>
        </motion.div>
      </section>

      {/* Section 4 — The Final Line */}
      <section className="w-full px-6 md:px-12 lg:px-24 pt-16 md:pt-24 pb-12" data-id="element-77">
        <div className="max-w-2xl mx-auto flex flex-col items-center text-center gap-8" data-id="element-78">
          <p className="text-[17px] md:text-lg text-ink-secondary" data-id="element-79">
            Thank you for considering RIA Catalyst. We appreciate the
            conversation.
          </p>
          <DiamondMark data-id="element-80" />
        </div>
      </section>

      {/* Footer */}
      <footer className="w-full px-6 md:px-12 lg:px-24 pb-10" data-id="element-81">
        <p className="text-center text-[12px] text-ink-secondary/70" data-id="element-82">
          RIA Catalyst is a product of DealFlow Advisory · © 2026
        </p>
      </footer>
    </div>;
}